import { PointerEvent, useEffect, useRef, useState } from 'react';

const MARGIN = 12;
const DRAG_THRESHOLD = 4;
// Hover alone shows a normal pointer cursor — only a sustained press (meaning
// "I want to move this", not "I want to click this") switches to the hand/move
// cursor, so a quick click never flashes the drag affordance.
const HOLD_DELAY = 180;

export type FloatSize = { width: number; height: number };
export type FloatPos = { x: number; y: number };

const clamp = (pos: FloatPos, size: FloatSize): FloatPos => {
    const maxX = Math.max(window.innerWidth - size.width - MARGIN, MARGIN);
    const maxY = Math.max(window.innerHeight - size.height - MARGIN, MARGIN);
    return {
        x: Math.min(Math.max(pos.x, MARGIN), maxX),
        y: Math.min(Math.max(pos.y, MARGIN), maxY),
    };
};

// Drag-anywhere-on-screen + remembered position (localStorage) + a cursor that
// stays a normal pointer until the user actually holds the button down long
// enough (or starts moving) to mean "drag", shared by every floating tour button.
export const useDraggableFloat = (storageKey: string, size: FloatSize, getDefaultPos: () => FloatPos) => {
    const load = (): FloatPos => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) return clamp(JSON.parse(raw), size);
        } catch {
            // Corrupt/blocked storage — fall back to the default spot.
        }
        return clamp(getDefaultPos(), size);
    };

    const [pos, setPos] = useState<FloatPos>(load);
    const [dragIntent, setDragIntent] = useState(false);
    const draggingRef = useRef(false);
    const movedRef = useRef(false);
    const startRef = useRef<{ mouseX: number; mouseY: number; boxX: number; boxY: number } | null>(null);
    const holdTimerRef = useRef<number | null>(null);

    useEffect(() => {
        const onResize = () => setPos((p) => clamp(p, size));
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPointerDown = (e: PointerEvent<HTMLElement>) => {
        draggingRef.current = true;
        movedRef.current = false;
        startRef.current = { mouseX: e.clientX, mouseY: e.clientY, boxX: pos.x, boxY: pos.y };
        e.currentTarget.setPointerCapture(e.pointerId);
        holdTimerRef.current = window.setTimeout(() => setDragIntent(true), HOLD_DELAY);
    };

    const onPointerMove = (e: PointerEvent<HTMLElement>) => {
        if (!draggingRef.current || !startRef.current) return;
        const dx = e.clientX - startRef.current.mouseX;
        const dy = e.clientY - startRef.current.mouseY;
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
            movedRef.current = true;
            setDragIntent(true);
        }
        setPos(clamp({ x: startRef.current.boxX + dx, y: startRef.current.boxY + dy }, size));
    };

    const endDrag = () => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        if (holdTimerRef.current) {
            window.clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        setDragIntent(false);
        setPos((p) => {
            try {
                localStorage.setItem(storageKey, JSON.stringify(p));
            } catch {
                // Ignore — the position just won't persist this session.
            }
            return p;
        });
    };

    return {
        pos,
        cursor: dragIntent ? 'grabbing' : 'pointer',
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
        },
        wasDragged: () => movedRef.current,
    };
};
